import React, { useEffect, useState } from 'react';
import { Box, Container, Heading, Text, VStack, Divider, SimpleGrid, Progress, Tabs, TabList, Tab, TabPanels, TabPanel, HStack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { motion } from 'framer-motion';
const MotionBox = motion(Box);
const pulseKeyframes = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 153, 255, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(0, 153, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 153, 255, 0); }
`;
const SkillBar = ({
  skill,
  level
}) => {
  const [progress, setProgress] = useState(0);
  const trackColor = 'rgba(0, 153, 255, 0.1)';
  useEffect(() => {
    const timer = setTimeout(() => {
      setProgress(level);
    }, 200);
    return () => clearTimeout(timer);
  }, [level]);
  return <Box mb={5} width="100%" data-id="element-271">
      <HStack justify="space-between" mb={1} data-id="element-272">
        <Text fontWeight="medium" fontFamily="mono" color="whiteAlpha.900" data-id="element-273">
          {skill}
        </Text>
        <Box px={2} py={0.5} borderRadius="md" bg="rgba(0, 153, 255, 0.1)" border="1px solid rgba(0, 153, 255, 0.3)" data-id="element-274">
          <Text fontSize="xs" fontFamily="mono" color="neonBlue.400" data-id="element-275">
            {level}%
          </Text>
        </Box>
      </HStack>
      <Box position="relative" h="8px" w="100%" bg={trackColor} borderRadius="md" overflow="hidden" data-id="element-276">
        <Box h="100%" w={`${progress}%`} bg="linear-gradient(90deg, #0099ff, #7700ff)" borderRadius="md" transition="width 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)" position="relative" _after={{
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '2px',
        height: '100%',
        bg: 'white',
        opacity: 0.7
      }} data-id="element-277" />
      </Box>
    </Box>;
};
export function Skills() {
  const backendSkills = [{
    name: 'Django/DRF',
    level: 95
  }, {
    name: 'Node.js/Express',
    level: 85
  }, {
    name: 'Python',
    level: 90
  }, {
    name: 'SQL/PostgreSQL',
    level: 90
  }, {
    name: 'MongoDB',
    level: 80
  }];
  const securitySkills = [{
    name: 'Penetration Testing',
    level: 90
  }, {
    name: 'OWASP Security Standards',
    level: 95
  }, {
    name: 'Vulnerability Assessment',
    level: 85
  }, {
    name: 'Security Auditing',
    level: 90
  }, {
    name: 'Authentication Systems',
    level: 95
  }];
  const devOpsSkills = [{
    name: 'Docker/Containerization',
    level: 85
  }, {
    name: 'CI/CD Pipelines',
    level: 80
  }, {
    name: 'AWS/Cloud Services',
    level: 75
  }, {
    name: 'Linux Administration',
    level: 90
  }, {
    name: 'Nginx/Apache',
    level: 85
  }];
  const frontendSkills = [{
    name: 'JavaScript/TypeScript',
    level: 80
  }, {
    name: 'React',
    level: 75
  }, {
    name: 'HTML/CSS',
    level: 85
  }, {
    name: 'Responsive Design',
    level: 80
  }];
  return <Box id="skills" py={20} width="100%" position="relative" bg="darkBg.900" data-id="element-278">
      {/* Circuit pattern background */}
      <Box position="absolute" top="0" left="0" right="0" bottom="0" opacity="0.05" zIndex="0" bgImage="url('https://images.unsplash.com/photo-1544197150-b99a580bb7a8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=80')" bgSize="cover" bgPosition="center" data-id="element-279" />
      <Container maxW="1200px" position="relative" zIndex="1" data-id="element-280">
        <VStack spacing={12} data-id="element-281">
          <VStack spacing={4} textAlign="center" maxW="800px" data-id="element-282">
            <MotionBox initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-283">
              <Heading as="h2" fontSize={{
              base: '3xl',
              md: '4xl'
            }} fontFamily="mono" position="relative" display="inline-block" data-id="element-284">
                <Box as="span" color="neonBlue.400" mr={2} data-id="element-285">
                  {'<'}
                </Box>
                Technical Skills
                <Box as="span" color="neonBlue.400" ml={2} data-id="element-286">
                  {' />'}
                </Box>
              </Heading>
            </MotionBox>
            <Divider width="100px" borderColor="neonBlue.500" borderWidth={2} opacity={0.6} data-id="element-287" />
            <Text fontSize="lg" color="whiteAlpha.800" data-id="element-288">
              Specialized expertise in backend development, security, and system
              architecture
            </Text>
          </VStack>
          <MotionBox initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} width="100%" data-id="element-289">
            <Box p={6} borderRadius="lg" bg="darkBg.800" border="1px solid rgba(0, 153, 255, 0.2)" boxShadow="0 0 20px rgba(0, 153, 255, 0.1)" data-id="element-290">
              <Tabs variant="soft-rounded" colorScheme="blue" width="100%" isLazy data-id="element-291">
                <TabList justifyContent="center" flexWrap="wrap" gap={4} mb={8} data-id="element-292">
                  {['Backend', 'Security', 'DevOps', 'Frontend'].map((tab, index) => <MotionBox key={tab} initial={{
                  opacity: 0,
                  y: -10
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.3,
                  delay: 0.1 * index
                }} data-id="element-293">
                        <Tab fontFamily="mono" _selected={{
                    color: 'white',
                    bg: 'neonBlue.600',
                    boxShadow: '0 0 15px rgba(0, 153, 255, 0.5)'
                  }} px={5} py={2} data-id="element-294">
                          {tab}
                        </Tab>
                      </MotionBox>)}
                </TabList>
                <TabPanels data-id="element-295">
                  <TabPanel data-id="element-296">
                    <MotionBox initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.5
                  }} data-id="element-297">
                      <SimpleGrid columns={{
                      base: 1,
                      md: 2
                    }} spacing={10} width="100%" data-id="element-298">
                        {backendSkills.map((skill, index) => <SkillBar key={index} skill={skill.name} level={skill.level} data-id="element-299" />)}
                      </SimpleGrid>
                    </MotionBox>
                  </TabPanel>
                  <TabPanel data-id="element-300">
                    <MotionBox initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.5
                  }} data-id="element-301">
                      <SimpleGrid columns={{
                      base: 1,
                      md: 2
                    }} spacing={10} width="100%" data-id="element-302">
                        {securitySkills.map((skill, index) => <SkillBar key={index} skill={skill.name} level={skill.level} data-id="element-303" />)}
                      </SimpleGrid>
                    </MotionBox>
                  </TabPanel>
                  <TabPanel data-id="element-304">
                    <MotionBox initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.5
                  }} data-id="element-305">
                      <SimpleGrid columns={{
                      base: 1,
                      md: 2
                    }} spacing={10} width="100%" data-id="element-306">
                        {devOpsSkills.map((skill, index) => <SkillBar key={index} skill={skill.name} level={skill.level} data-id="element-307" />)}
                      </SimpleGrid>
                    </MotionBox>
                  </TabPanel>
                  <TabPanel data-id="element-308">
                    <MotionBox initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.5
                  }} data-id="element-309">
                      <SimpleGrid columns={{
                      base: 1,
                      md: 2
                    }} spacing={10} width="100%" data-id="element-310">
                        {frontendSkills.map((skill, index) => <SkillBar key={index} skill={skill.name} level={skill.level} data-id="element-311" />)}
                      </SimpleGrid>
                    </MotionBox>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </MotionBox>
        </VStack>
      </Container>
    </Box>;
}